import React, { useState, useRef, useEffect } from 'react';
import Modal from './Modal';
import DocViewer from './DocViewer';

const BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/api\/?$/, '');

const getFileUrl = (filename) => `${BASE_URL}/uploads/inspections/${filename}`;

const DAMAGE_FIELDS = [
  { key: 'damageTop',    label: 'Damage Top' },
  { key: 'damageBottom', label: 'Damage Bottom' },
  { key: 'damageFront',  label: 'Damage Front' },
  { key: 'damageBack',   label: 'Damage Back' },
  { key: 'damageSides',  label: 'Damage from Sides' },
];

const empty = () => ({ damageTop: '', damageBottom: '', damageFront: '', damageBack: '', damageSides: '', notes: '' });

const InspectionForm = ({ isOpen, onClose, type, vehicle, onSaved }) => {
  const [form, setForm] = useState(empty());
  const [nothingToReport, setNothingToReport] = useState(false);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) { setForm(empty()); setFiles([]); setNothingToReport(false); }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const addFiles = (incoming) => {
    const allowed = Array.from(incoming).filter((f) => {
      if (!/\.(pdf|jpg|jpeg|png)$/i.test(f.name)) return false;
      if (f.size > 20 * 1024 * 1024) { alert(`${f.name} exceeds 20MB limit.`); return false; }
      return true;
    });
    setFiles((prev) => [...prev, ...allowed]);
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSaved(nothingToReport ? { ...empty(), nothingToReport: true } : { ...form, nothingToReport: false }, files);
      onClose();
    } catch {
      alert('Error saving inspection. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const title = type === 'checkin' ? 'Check In — Take Inspection' : 'Check Out — Take Inspection';

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
        <form onSubmit={handleSubmit}>
          {DAMAGE_FIELDS.map(({ key, label }) => (
            <div className="form-group" key={key}>
              <label className="form-label">{label}</label>
              <input
                type="text"
                name={key}
                className="form-input"
                value={nothingToReport ? '' : form[key]}
                onChange={handleChange}
                placeholder="Enter details…"
                disabled={nothingToReport}
                style={nothingToReport ? { opacity: 0.4, pointerEvents: 'none' } : {}}
              />
            </div>
          ))}

          <div className="form-group">
            <label className="form-label">Notes</label>
            <input
              type="text"
              name="notes"
              className="form-input"
              value={nothingToReport ? '' : form.notes}
              onChange={handleChange}
              placeholder="Additional notes…"
              disabled={nothingToReport}
              style={nothingToReport ? { opacity: 0.4, pointerEvents: 'none' } : {}}
            />
          </div>

          {/* Nothing to report checkbox */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 0', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={nothingToReport}
              onChange={(e) => {
                setNothingToReport(e.target.checked);
                if (e.target.checked) setForm(empty());
              }}
              style={{ width: 16, height: 16, accentColor: '#10b981', cursor: 'pointer' }}
            />
            <span style={{ fontSize: 14, color: 'var(--text-2)', fontWeight: 500 }}>
              Nothing to {type === 'checkin' ? 'check in' : 'check out'}
            </span>
          </label>

          {/* Multi-file picker */}
          <div className="form-group" style={{ marginTop: 4 }}>
            <label className="form-label">Proof Documents / Photos</label>

            {files.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                {files.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0077b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span style={{ flex: 1, fontSize: 12, color: '#0077b6', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <span style={{ fontSize: 11, color: '#64748b', flexShrink: 0 }}>{(f.size / 1024).toFixed(0)} KB</span>
                    <button type="button" onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2, flexShrink: 0 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              style={{ border: `2px dashed ${dragOver ? '#0077b6' : '#cbd5e1'}`, borderRadius: 10, padding: '20px', textAlign: 'center', cursor: 'pointer', background: dragOver ? '#f0f9ff' : '#f8fafc', transition: 'all 0.15s' }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>Click or drag files here</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>PDF, JPG, PNG — max 20MB each — multiple allowed</div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => addFiles(e.target.files)}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving…' : type === 'checkin' ? 'Save Check In' : 'Save Check Out'}
            </button>
          </div>
        </form>
      </Modal>

      <DocViewer url={previewDoc?.url} filename={previewDoc?.filename} onClose={() => setPreviewDoc(null)} />
    </>
  );
};

export default InspectionForm;
